import React, { useState } from "react";
import axios from "axios";
import {
  Box, Typography, TextField, Button, CircularProgress, Chip,
  IconButton, Tooltip,
} from "@mui/material";
import styled, { keyframes, css } from "styled-components";
import { FONT_BODY, FONT_DISPLAY, TYPE, RADIUS, COLORS } from "../../styles/recruiterTheme";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const FormPanel = styled(Box)`
  ${css`animation: ${fadeSlideIn} 0.4s ease both;`}
  max-width: 640px;
`;

const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 !important;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: #8B7DB5 !important;
    font-weight: 500 !important;
    margin-top: 4px !important;
  }
`;

const SectionLabel = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.overline}px !important;
    font-weight: 700 !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #8B7DB5 !important;
    margin-bottom: 8px !important;
  }
`;

const KeywordGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
  & > *:last-child { grid-column: 1 / -1; }
`;

const PrimaryButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #6750A4, #4F378B) !important;
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.body}px !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 36px;
    padding: 0 16px !important;
    text-transform: none !important;
    box-shadow: 0 4px 14px rgba(79,55,139,0.3) !important;
    transition: all 0.2s ease !important;
    &:hover { transform: translateY(-1px); box-shadow: 0 7px 20px rgba(79,55,139,0.4) !important; }
    &.Mui-disabled { opacity: 0.55; color: #fff !important; }
  }
`;

const ResultCard = styled(Box)`
  margin-top: 24px;
  padding: 16px 24px;
  border-radius: ${RADIUS.md}px;
  border: 1.5px solid ${p => p.tone === "pass" ? "#8CC7A0" : "#F5A9A0"};
  background: ${p => p.tone === "pass" ? "#F0FFF4" : "#FFF5F5"};
`;

const ResultTitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.body}px !important;
    color: #3D2B6B !important;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px !important;
  }
`;

const ResultBody = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    line-height: 1.5 !important;
    color: #6B5B95 !important;
    margin-bottom: 8px !important;
  }
`;

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: `${RADIUS.sm}px`,
    fontFamily: FONT_BODY,
    fontSize: TYPE.body,
    height: 40,
  },
  "& .MuiInputLabel-root": { fontFamily: FONT_BODY, fontSize: TYPE.body },
};

const KEYWORD_PLACEHOLDERS = [
  "e.g. payment gateways",
  "e.g. fraud detection",
  "e.g. kyc compliance",
  "e.g. core banking systems",
  "e.g. pci dss compliance",
];

function DomainOnboardingForm({ onBack, onSuccess, onSetSnackbar }) {
  const [domainName, setDomainName] = useState("");
  const [keywords, setKeywords] = useState(["", "", "", "", ""]);
  const [status, setStatus] = useState("idle"); // idle | loading | pass | fail | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const isLoading = status === "loading";

  function updateKeyword(index, value) {
    setKeywords(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (isLoading) return;
    setErrorMsg("");
    setResult(null);

    const trimmedDomain = domainName.trim();
    const trimmedKeywords = keywords.map(k => k.trim());

    if (!trimmedDomain || trimmedKeywords.some(k => !k)) {
      setErrorMsg("Fill in the domain name and all 5 keywords.");
      return;
    }

    setStatus("loading");
    try {
      const response = await axios.post(
        process.env.REACT_APP_ONBOARD_DOMAIN_API,
        { domain_name: trimmedDomain, keywords: trimmedKeywords }
      );
      const data = response.data;
      setResult(data);
      setStatus(data.verdict === "pass" ? "pass" : "fail");

      if (data.verdict === "pass") {
        onSetSnackbar?.(`✅ Domain "${data.domain_name}" added`);
        setDomainName("");
        setKeywords(["", "", "", "", ""]);
        onSuccess?.(data);
      }
    } catch (err) {
      setStatus("error");
      const data = err.response?.data;
      setResult(data || { error: err.message });
      onSetSnackbar?.("Failed to add domain.");
    }
  }

  return (
    <FormPanel>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton
          onClick={onBack}
          sx={{ width: 36, height: 36, flexShrink: 0, background: "#F3EEF9", borderRadius: `${RADIUS.sm}px`, "&:hover": { background: "#EDE7F6" } }}
        >
          <ArrowBackIcon sx={{ color: "#4F378B" }} />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <PageTitle>Add a Hiring Domain</PageTitle>
          <PageSubtitle>
            Name a domain and give five example skills — the model validates them
            and expands them into a full keyword set for resume matching.
          </PageSubtitle>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <SectionLabel>Domain name</SectionLabel>
          <TextField
            fullWidth
            size="small"
            value={domainName}
            onChange={(e) => setDomainName(e.target.value)}
            placeholder="e.g. Fintech"
            sx={textFieldSx}
          />
        </Box>

        <SectionLabel>5 seed keywords</SectionLabel>
        <KeywordGrid>
          {keywords.map((kw, i) => (
            <TextField
              key={i}
              size="small"
              value={kw}
              onChange={(e) => updateKeyword(i, e.target.value)}
              placeholder={KEYWORD_PLACEHOLDERS[i]}
              sx={textFieldSx}
            />
          ))}
        </KeywordGrid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <PrimaryButton type="submit" disabled={isLoading}>
          {isLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "#fff" }} />
              Validating & generating…
            </Box>
          ) : (
            "Generate & Add Domain"
          )}
        </PrimaryButton>
        </Box>

        {errorMsg && (
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.error, mt: 1, textAlign: "right" }}>
            {errorMsg}
          </Typography>
        )}
      </form>

      {status === "pass" && result && (
        <ResultCard tone="pass">
          <ResultTitle>
            <CheckCircleIcon sx={{ fontSize: 18, color: "#2D7D46" }} />
            Added "{result.domain_name}" ({result.domain_slug})
          </ResultTitle>
          <ResultBody>{result.keyword_count} keywords generated and saved.</ResultBody>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {result.keywords?.map((k, i) => (
              <Chip
                key={i}
                label={k}
                size="small"
                sx={{
                  fontFamily: FONT_BODY,
                  fontSize: TYPE.caption,
                  fontWeight: 600,
                  height: 24,
                  borderRadius: `${RADIUS.pill}px`,
                  background: "#EDE7F6",
                  color: "#4F378B",
                }}
              />
            ))}
          </Box>
        </ResultCard>
      )}

      {status === "fail" && result && (
        <ResultCard tone="fail">
          <ResultTitle>
            <CancelIcon sx={{ fontSize: 18, color: "#B3261E" }} />
            Validation failed for "{result.domain_name}"
          </ResultTitle>
          <ResultBody>
            At least 4 of the 5 seed keywords need to pass. Nothing was saved.
          </ResultBody>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {result.reasons?.map((r, i) => (
              <Typography
                key={i}
                component="li"
                sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, lineHeight: 1.5, color: "#3D2B6B", mb: 0.5 }}
              >
                <strong>{r.keyword}</strong> — {r.reason}
              </Typography>
            ))}
          </Box>
        </ResultCard>
      )}

      {status === "error" && result && (
        <ResultCard tone="fail">
          <ResultTitle>
            <ErrorIcon sx={{ fontSize: 18, color: "#B3261E" }} />
            Request failed
          </ResultTitle>
          <ResultBody>
            {result.error
              || (typeof result.detail === "string" ? result.detail : result.detail && JSON.stringify(result.detail))
              || "Unknown error."}
          </ResultBody>
        </ResultCard>
      )}
    </FormPanel>
  );
}

export default DomainOnboardingForm;